import { XMarkIcon } from '@heroicons/react/20/solid';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import React from 'react';

import { AnimatePresence, Overlay, SheetPanel } from './Animations';
import { cn } from './cn';
import { isEventInsideToaster } from './Toaster';

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  width?: string;
  /** When false, clicking the overlay/outside does not dismiss the sheet (Escape still works). Default true. */
  dismissOnOutsideClick?: boolean;
}

export const Sheet = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  width = '640px',
  dismissOnOutsideClick = true,
}: SheetProps) => {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <Overlay className="fixed inset-0 z-50 bg-black/40" />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              asChild
              onInteractOutside={(event) => {
                // Toasts are portaled next to this dialog — interacting with them must not dismiss it.
                if (isEventInsideToaster(event.detail.originalEvent)) {
                  event.preventDefault();
                  return;
                }
                if (!dismissOnOutsideClick) {
                  event.preventDefault();
                  return;
                }
                onOpenChange(false);
              }}
            >
              <SheetPanel
                className={cn(
                  'fixed inset-y-0 right-0 z-50 flex flex-col bg-surface shadow-2xl border-l border-border',
                  className
                )}
                style={{ width }}
              >
                {title && (
                  <div className="flex items-start justify-between border-b border-border px-7 py-5">
                    <div className="flex flex-col gap-1">
                      <DialogPrimitive.Title className="font-heading text-xl font-semibold text-text-primary tracking-tight">
                        {title}
                      </DialogPrimitive.Title>
                      {description && (
                        <DialogPrimitive.Description className="text-sm text-text-muted">
                          {description}
                        </DialogPrimitive.Description>
                      )}
                    </div>
                    <DialogPrimitive.Close asChild>
                      <button
                        type="button"
                        className="mt-0.5 rounded-md p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-hover cursor-pointer bg-transparent border-none transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                        aria-label="Close"
                      >
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </DialogPrimitive.Close>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto px-7 py-6">{children}</div>
                {footer && (
                  <div className="flex items-center gap-3 border-t border-border bg-surface/80 backdrop-blur-md px-7 py-4">
                    {footer}
                  </div>
                )}
              </SheetPanel>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
};
